import dayjs from 'dayjs'
import { useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Appointments } from '@devexpress/dx-react-scheduler-material-ui'
import { styled } from '@mui/material/styles'
import { Typography, Stack, Chip, Box, Tooltip } from '@mui/material'
import GroupIcon from '@mui/icons-material/Group'
import PersonIcon from '@mui/icons-material/Person'
import WarningIcon from '@mui/icons-material/Warning'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import { CalendarShiftData } from '../model/types'
import { ShiftDetails } from './ShiftDetails'
import { Shift } from '@/core/models/shift.model'

const StyledShiftAppointment = styled(Box, {
  shouldForwardProp: (prop) => !['isPast', 'isStaffed', 'isPartiallyFilled', 'isEmpty'].includes(String(prop)),
})<{
  isPast: boolean
  isStaffed?: boolean
  isPartiallyFilled?: boolean
  isEmpty?: boolean
}>(({ isPast, isPartiallyFilled = false, isEmpty = false, theme }) => {
  // Determine background color based on status
  let backgroundColor = '#4CAF50' // Default green for fully staffed
  let hoverColor = '#45a049'
  let borderColor = '#1B5E20'
  let borderStyle = 'solid'

  if (isPast) {
    backgroundColor = '#E0E0E0' // Gray for past shifts
    hoverColor = '#E0E0E0'
    borderColor = '#9E9E9E'
    borderStyle = 'solid'
  } else if (isPartiallyFilled) {
    backgroundColor = '#FFC107' // Yellow for partially filled
    hoverColor = '#FFA000'
    borderColor = '#FF8F00'
    borderStyle = 'solid'
  } else if (isEmpty) {
    backgroundColor = '#F44336' // Red for empty shifts
    hoverColor = '#D32F2F'
    borderColor = '#B71C1C'
    borderStyle = 'dashed'
  }

  return {
    height: '100%',
    backgroundColor,
    opacity: isPast ? 0.8 : 1,
    '&:hover': {
      backgroundColor: hoverColor,
      opacity: isPast ? 0.8 : 0.9,
    },
    borderRadius: theme.shape.borderRadius,
    border: `2px ${borderStyle} ${borderColor}`,
    zIndex: 2,
    cursor: isPast ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    color: isPast ? theme.palette.text.secondary : theme.palette.getContrastText(backgroundColor),
  }
})

function prepareData(data: CalendarShiftData) {
  const staffCount = data.assignedStaff?.length || 0
  const totalRequired = data.requiredStaffCount || 0
  const remainingSpots = totalRequired - staffCount
  const isFilled = staffCount === totalRequired
  const isPartiallyFilled = staffCount > 0 && staffCount < totalRequired
  const isEmpty = staffCount === 0
  return { staffCount, totalRequired, remainingSpots, isFilled, isPartiallyFilled, isEmpty }
}

function getStatusColor(data: CalendarShiftData) {
  const { isFilled, isPartiallyFilled } = prepareData(data)
  if (data.isPast) return 'text.disabled'
  if (isFilled) return 'success.main'
  if (isPartiallyFilled) return 'warning.main'
  return 'error.main'
}

function getStatusText(data: CalendarShiftData) {
  const { isFilled, isPartiallyFilled, remainingSpots } = prepareData(data)
  if (data.isPast) return t`Past`
  if (isFilled) return t`Fully Staffed`
  if (isPartiallyFilled) return t`${remainingSpots} spots left`
  return t`Unstaffed`
}

function getStatusIcon(data: CalendarShiftData) {
  const { isFilled, isPartiallyFilled } = prepareData(data)
  if (data.isPast) return null
  if (isFilled) return <CheckCircleIcon fontSize="small" color="success" />
  if (isPartiallyFilled) return <WarningIcon fontSize="small" color="warning" />
  return <WarningIcon fontSize="small" color="error" />
}

type ShiftAppointmentProps = Omit<Appointments.AppointmentProps, 'data'> & {
  data: CalendarShiftData
  planningId: string
  shift: Shift
}

/**
 * Renders a shift appointment in the calendar
 * Shows assigned staff and remaining slots with enhanced visual indicators
 */
export function ShiftAppointment({ data, planningId, shift, ...rest }: ShiftAppointmentProps) {
  useLingui()

  const [isShiftDetailsOpen, setIsShiftDetailsOpen] = useState(false)

  const { isFilled, isPartiallyFilled, isEmpty } = prepareData(data)

  const formattedStartTime = dayjs(data.startDate).format('HH:mm')
  const formattedEndTime = dayjs(data.endDate).format('HH:mm')
  const timeRange = `${formattedStartTime} - ${formattedEndTime}`

  return (
    <>
      <Appointments.Appointment {...rest} data={data}>
        <StyledShiftAppointment
          isPast={data.isPast}
          isStaffed={isFilled}
          isPartiallyFilled={isPartiallyFilled}
          isEmpty={isEmpty}
          onClick={() => {
            if (data.isPast) return
            setIsShiftDetailsOpen(true)
          }}
        >
          <Box height="100%" padding={(theme) => theme.spacing(1)} overflow="visible">
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {data.location}
                </Typography>
                {getStatusIcon(data)}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {timeRange}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <GroupIcon fontSize="small" color="action" />
                <Typography variant="body2" color={getStatusColor(data)}>
                  {getStatusText(data)}
                </Typography>
              </Box>

              {data.assignedStaff && data.assignedStaff.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <Trans>Assigned staff</Trans>
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {data.assignedStaff.map((staff) => (
                      <Tooltip key={staff._id} title={`${staff.userInfo.name} ${staff.userInfo.surname}`}>
                        <Chip
                          icon={<PersonIcon fontSize="small" />}
                          label={`${staff.userInfo.name} ${staff.userInfo.surname}`}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            borderRadius: '4px',
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '80px',
                            },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Box>
        </StyledShiftAppointment>
      </Appointments.Appointment>
      <ShiftDetails open={isShiftDetailsOpen} onClose={() => setIsShiftDetailsOpen(false)} shift={shift} planningId={planningId} />
    </>
  )
}
